/**
 * LandingCalculator — interactive "how much can I spend this week?" widget.
 *
 * Pure client-side math (mirrors the simplified core logic of
 * services/cash_calc.py). The point is to produce an "aha-moment" right
 * on the landing without any account creation. If the visitor likes
 * the number, the CTA below kicks them into /register where the real
 * onboarding takes over.
 *
 * Formula (intentionally simplified for the marketing surface):
 *     monthly_net  = monthly_income - committed_payments
 *     buffer       = current_cash + expected_receivables * 0.6  (60% weighting reflects collection uncertainty)
 *     usable_week  = max(0, (monthly_net + buffer) / WEEKS_AHEAD)
 *     safe_week    = usable_week * SAFETY_FACTOR (0.7)
 *
 * The 0.6 receivables weighting + 0.7 safety factor are conservative
 * defaults — the in-app calculation uses per-receivable probability
 * weights and a more nuanced safety model. We keep both numbers
 * visible (safe vs. raw) so the visitor sees Lumfi gives them an
 * honest, conservative number, not magic.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Calculator, ArrowRight, ShieldCheck, AlertTriangle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";

const WEEKS_AHEAD = 4;
const RECEIVABLES_WEIGHT = 0.6;
const SAFETY_FACTOR = 0.7;

const COUNTRY_DEFAULTS = {
  CO: { currency: "COP", locale: "es-CO", income: 5_800_000, cash: 2_200_000, committed: 4_100_000, expected: 3_200_000 },
  MX: { currency: "MXN", locale: "es-MX", income: 48_000,    cash: 22_000,    committed: 34_000,    expected: 28_000 },
  AR: { currency: "ARS", locale: "es-AR", income: 1_950_000, cash: 720_000,   committed: 1_400_000, expected: 1_080_000 },
  BR: { currency: "BRL", locale: "pt-BR", income: 11_500,    cash: 4_800,     committed: 8_200,     expected: 6_400 },
  CL: { currency: "CLP", locale: "es-CL", income: 2_300_000, cash: 850_000,   committed: 1_600_000, expected: 1_280_000 },
  PE: { currency: "PEN", locale: "es-PE", income: 10_200,    cash: 3_900,     committed: 7_300,     expected: 5_800 },
  UY: { currency: "UYU", locale: "es-UY", income: 115_000,   cash: 45_000,    committed: 82_000,    expected: 65_000 },
};

function fmt(amount, locale, currency) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: amount >= 1000 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${Math.round(amount).toLocaleString()}`;
  }
}

/** Group-only digit formatter — no currency code, no decimals. Used as
 * the display value of the editable inputs so the user sees thousands
 * separators while they type without the cursor jumping. */
function fmtNumberOnly(amount, locale) {
  try {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(amount);
  } catch {
    return Math.round(amount).toLocaleString();
  }
}

/** Editable numeric field used by <LandingCalculator>.
 *
 * Defined at module scope (NOT inside LandingCalculator) so React keeps
 * the same DOM node across re-renders. Previously this lived inline,
 * which made every keystroke remount the input element and dropped the
 * cursor.
 *
 * Cursor preservation strategy:
 *   1. Local string state `text` = formatted digits the user sees.
 *   2. On each change we strip non-digits, count digits-before-caret,
 *      reformat, and restore the caret to the same digit-relative
 *      position. Separators shifting underneath the cursor never
 *      moves the visible caret.
 */
function CalcField({ k, label, value, locale, currency, onChange }) {
  const inputRef = useRef(null);
  const [text, setText] = useState(() => fmtNumberOnly(value, locale));

  // Sync external numeric value → local string when it changes from outside
  // (e.g. country preset reset), but never while the user is actively typing.
  useEffect(() => {
    const expected = fmtNumberOnly(value, locale);
    if (document.activeElement !== inputRef.current && expected !== text) {
      setText(expected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, locale]);

  const handleChange = (e) => {
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    const digitsBefore = (el.value.slice(0, caret).match(/\d/g) || []).length;
    const digits = el.value.replace(/\D/g, "");
    const num = digits ? Number(digits) : 0;
    const next = digits ? fmtNumberOnly(num, locale) : "";
    setText(next);
    onChange(num);
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      let pos = 0;
      let found = 0;
      while (pos < next.length && found < digitsBefore) {
        if (/\d/.test(next[pos])) found += 1;
        pos += 1;
      }
      try {
        inputRef.current.setSelectionRange(pos, pos);
      } catch { /* ignore — input doesn't support selection */ }
    });
  };

  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <div className="mt-1 relative">
        <input /* design-system: ALLOW legacy-todo */
          ref={inputRef}
          inputMode="numeric"
          value={text}
          onChange={handleChange}
          data-testid={`calc-input-${k}`}
          className="w-full h-11 rounded-xl border border-slate-200 bg-white px-3 pr-12 font-mono-num text-base font-semibold text-slate-900 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition-colors"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 uppercase tabular-nums">
          {currency}
        </span>
      </div>
    </label>
  );
}


export function LandingCalculator({ country = "CO", lang = "es" }) {
  const isEs = lang === "es";
  const isEn = lang === "en";
  const cfg = COUNTRY_DEFAULTS[country] || COUNTRY_DEFAULTS.CO;
  const [values, setValues] = useState(() => ({
    income: cfg.income,
    cash: cfg.cash,
    committed: cfg.committed,
    expected: cfg.expected,
  }));

  // Re-prime defaults when the user changes country (via the floating
  // switcher in <LandingPricing>) — we listen on storage events that
  // session-cache uses so the change is reactive without a global store.
  useEffect(() => {
    setValues({
      income: cfg.income,
      cash: cfg.cash,
      committed: cfg.committed,
      expected: cfg.expected,
    });
  }, [cfg.income, cfg.cash, cfg.committed, cfg.expected]);

  const result = useMemo(() => {
    const monthlyNet = values.income - values.committed;
    const buffer = values.cash + values.expected * RECEIVABLES_WEIGHT;
    const usableWeek = Math.max(0, (monthlyNet + buffer) / WEEKS_AHEAD);
    const safeWeek = usableWeek * SAFETY_FACTOR;
    // Risk bucket
    let risk = "low";
    if (monthlyNet < 0) risk = "high";
    else if (buffer < Math.abs(monthlyNet) * 1.5) risk = "mid";
    return { safeWeek, usableWeek, monthlyNet, buffer, risk };
  }, [values]);

  const setField = (key, num) => {
    setValues((v) => ({ ...v, [key]: num }));
  };

  const labels = isEs ? {
    title: "¿Cuánto puedo gastar esta semana?",
    sub: "Calcula tu límite seguro semanal en 10 segundos. Sin registrarte.",
    income: "Ingreso típico del mes",
    cash: "Plata que tengo hoy",
    committed: "Pagos comprometidos del mes",
    expected: "Cobros esperados del mes",
    safe: "Puedes gastar esta semana",
    safe_sub: "Con margen de seguridad — el número honesto",
    raw: "Tope si no priorizas colchón",
    cta: "Empezar gratis con tu plan real",
    risk_low: "Riesgo bajo",
    risk_mid: "Riesgo medio",
    risk_high: "Riesgo alto",
    footnote: "Cálculo orientativo. En la app sumamos probabilidad por cobro, FX automático y proyección por semanas reales.",
  } : isEn ? {
    title: "How much can I spend this week?",
    sub: "Calculate your safe weekly limit in 10 seconds. No signup.",
    income: "Typical monthly income",
    cash: "Cash on hand today",
    committed: "Committed payments this month",
    expected: "Expected receivables this month",
    safe: "You can spend this week",
    safe_sub: "With safety margin — the honest number",
    raw: "Hard cap if you skip the buffer",
    cta: "Start free with your real plan",
    risk_low: "Low risk",
    risk_mid: "Mid risk",
    risk_high: "High risk",
    footnote: "Indicative calculation. In-app we weight each receivable by likelihood, auto-handle FX and forecast by real weeks.",
  } : {
    title: "Quanto posso gastar esta semana?",
    sub: "Calcule seu limite semanal seguro em 10 segundos. Sem cadastro.",
    income: "Renda típica do mês",
    cash: "Dinheiro disponível hoje",
    committed: "Pagamentos comprometidos do mês",
    expected: "Recebíveis esperados do mês",
    safe: "Pode gastar esta semana",
    safe_sub: "Com margem de segurança — o número honesto",
    raw: "Teto sem reserva",
    cta: "Começar grátis com seu plano real",
    risk_low: "Risco baixo",
    risk_mid: "Risco médio",
    risk_high: "Risco alto",
    footnote: "Cálculo indicativo. No app ponderamos cada recebível, FX automático e projeção por semanas reais.",
  };

  const riskMeta = {
    low:  { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ShieldCheck, label: labels.risk_low },
    mid:  { cls: "bg-amber-50 text-amber-700 border-amber-200",       icon: AlertTriangle, label: labels.risk_mid },
    high: { cls: "bg-rose-50 text-rose-700 border-rose-200",          icon: AlertTriangle, label: labels.risk_high },
  }[result.risk];

  // Inline render with stable function identity — preserves the same
  // CalcField DOM node across re-renders so the input keeps its caret
  // mid-typing. Earlier versions used a `<Field>` wrapper component
  // defined inside the parent, which got recreated on every render
  // and remounted the input on every keystroke.
  const renderField = (k, label) => (
    <CalcField
      key={k}
      k={k}
      label={label}
      value={values[k]}
      locale={cfg.locale}
      currency={cfg.currency}
      onChange={(num) => setField(k, num)}
    />
  );

  return (
    <div className="relative" data-testid="landing-calculator">
      {/* decorative glow */}
      <div className="absolute -inset-4 bg-gradient-to-br from-emerald-100/40 via-transparent to-amber-100/40 blur-2xl pointer-events-none" />
      <div className="relative bg-white rounded-3xl border border-cream-300 p-6 lg:p-10 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)]">
        <div className="flex items-start gap-3 mb-1">
          <div className="h-9 w-9 rounded-xl bg-emerald-500/10 text-emerald-700 flex items-center justify-center shrink-0">
            <Calculator className="h-4.5 w-4.5" />
          </div>
          <div>
            <h3 className="font-serif-display text-2xl lg:text-3xl font-medium text-slate-900 leading-tight">{labels.title}</h3>
            <p className="text-sm text-slate-500 mt-1">{labels.sub}</p>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {renderField("income",    labels.income)}
          {renderField("cash",      labels.cash)}
          {renderField("committed", labels.committed)}
          {renderField("expected",  labels.expected)}
        </div>

        {/* Result */}
        <div className="mt-6 rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-5 lg:p-6 relative overflow-hidden">
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-emerald-400/20 blur-2xl" />
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">{labels.safe}</p>
          <p className="font-mono-num text-3xl lg:text-5xl font-bold mt-1 tabular-nums" data-testid="calc-result-safe">
            {fmt(result.safeWeek, cfg.locale, cfg.currency)}
          </p>
          <p className="text-[11px] mt-1 opacity-80">{labels.safe_sub}</p>
          <div className="mt-4 pt-3 border-t border-emerald-400/40 flex items-center justify-between text-[11px] flex-wrap gap-2">
            <span className="opacity-80">{labels.raw}: <span className="font-mono-num font-semibold opacity-100">{fmt(result.usableWeek, cfg.locale, cfg.currency)}</span></span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${riskMeta.cls.replace("bg-emerald-50","bg-white/95").replace("bg-amber-50","bg-white/95").replace("bg-rose-50","bg-white/95")}`} data-testid="calc-result-risk">
              <riskMeta.icon className="h-3 w-3" />
              {riskMeta.label}
            </span>
          </div>
        </div>

        <Link to="/register" className="block mt-5">
          <Button
            data-testid="calc-cta"
            size="lg"
            className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white gap-2 h-12 text-base font-semibold"
          >
            <Sparkles className="h-4 w-4" />
            {labels.cta}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>

        <p className="mt-4 text-[10px] text-slate-400 leading-relaxed">
          {labels.footnote}
        </p>
      </div>
    </div>
  );
}
